import { parseLooseFrDateTime } from '@workspace/utils';
import { differenceInMonths } from 'date-fns';
import { useFormContext, useWatch } from 'react-hook-form';
import { OUI_NON_STRING_OPTIONS } from './options';
import { BulleAide } from '../../bulle-aide';
import { Question } from '../../question';
import { FieldRadioGroup, FieldSelectNative } from '../../smart-form';
import { Star } from '../../../icons-amo';

/*
 * Contenus de questions (à placer dans un <Question> de l'app). Portage de legacy
 * `@amo/core/components/questions/ValeurCrmMoto|ValeurCrmAuto` et avenant `Antecedents/CrmMoto50Plus3Ans|CrmAuto50Plus3Ans`.
 */

export type TypeCrm = 'moto' | 'auto';

const CONDUCTEUR = 'DemandeTarif.ListePersonnes.0';
const datePermisPath = (index: number) => `${CONDUCTEUR}.ListePermis.${index}.DatePermis`;

export const CRM_FIELDS = {
  moto: {
    VALEUR: `${CONDUCTEUR}.ValeurCrmMoto`,
    PLUS_3_ANS: `${CONDUCTEUR}.CrmMoto50Plus3Ans`,
    FIRST_VALEUR: 'firstValeurCrmMoto',
  },
  auto: {
    VALEUR: `${CONDUCTEUR}.ValeurCrmAuto`,
    PLUS_3_ANS: `${CONDUCTEUR}.CrmAuto50Plus3Ans`,
    FIRST_VALEUR: 'firstValeurCrmAuto',
  },
} as const;

/* ---------- Logique pure (legacy `getCRM`, `getCRMMotoMin`, `getCRMAutoMin`) ---------- */

/** CRM théorique selon l'ancienneté d'un permis (1 si date absente/invalide ou < 10 mois). */
export const getCrmByDatePermis = (datePermis: unknown, now: Date = new Date()): number => {
  const date = parseLooseFrDateTime(datePermis);
  if (!date) return 1;

  const mois = differenceInMonths(now, date);
  if (mois > 154) return 0.5;
  if (mois > 142) return 0.51;
  if (mois > 130) return 0.54;
  if (mois > 118) return 0.57;
  if (mois > 106) return 0.6;
  if (mois > 94) return 0.64;
  if (mois > 82) return 0.68;
  if (mois > 70) return 0.72;
  if (mois > 58) return 0.76;
  if (mois > 46) return 0.8;
  if (mois > 34) return 0.85;
  if (mois > 22) return 0.9;
  if (mois >= 10) return 0.95;
  return 1;
};

/** CRM moto minimum : le meilleur des permis A1, A2, A et B (le AM n'est pas pris en compte). */
export const getCrmMotoMin = (
  { datePermisA1, datePermisA2, datePermisA, datePermisB }: { datePermisA1?: unknown; datePermisA2?: unknown; datePermisA?: unknown; datePermisB?: unknown },
  now: Date = new Date(),
) => Math.min(...[datePermisA1, datePermisA2, datePermisA, datePermisB].map((date) => getCrmByDatePermis(date, now)));

/** CRM auto minimum : calculé sur le permis B. */
export const getCrmAutoMin = (datePermisB: unknown, now: Date = new Date()) => getCrmByDatePermis(datePermisB, now);

/** Options du select bonus-malus : 0.50 à 2.06 par pas de 0.01. */
export const CRM_OPTIONS = Array.from({ length: 157 }, (_, index) => {
  const code = (0.5 + index * 0.01).toFixed(2);
  if (code === '1.00') return { value: code, label: '1 soit ni bonus ni malus' };
  const bonusMalus = index < 50 ? `${50 - index}% de bonus` : `${index - 50}% de malus`;
  return { value: code, label: `${code} soit ${bonusMalus}` };
});

/* ---------- Composants ---------- */

interface CrmContentProps {
  type: TypeCrm;
}

/**
 * Bonus-malus moto ou auto. Un changement vide la question « 0.50 depuis plus de 3 ans » et mémorise la valeur
 * saisie (`firstValeurXxx`, restaurée par « assuré : oui »). Avertissement si inférieur au CRM calculé sur les permis.
 */
export function ValeurCrmContent({ type }: CrmContentProps) {
  const { setValue } = useFormContext();
  const fields = CRM_FIELDS[type];
  const [valeur, datePermisA1, datePermisA2, datePermisA, datePermisB] = useWatch({
    name: [fields.VALEUR, datePermisPath(1), datePermisPath(2), datePermisPath(3), datePermisPath(4)],
  });

  const minCrm =
    type === 'moto' ? getCrmMotoMin({ datePermisA1, datePermisA2, datePermisA, datePermisB }) : getCrmAutoMin(datePermisB);

  return (
    <div className="w-full flex flex-col items-center space-y-4">
      <Question.Header>
        <Star />
        <Question.Title>
          {type === 'moto' ? (
            <>
              Quel est votre <b>bonus 2-roues</b> actuel ?
            </>
          ) : (
            <>
              Quel est votre <b>bonus auto</b> actuel ?
            </>
          )}
        </Question.Title>
      </Question.Header>
      <div className="w-full max-w-sm">
        <FieldSelectNative
          name={fields.VALEUR}
          label=""
          placeholder="Sélectionnez votre bonus-malus"
          options={CRM_OPTIONS}
          validation={{
            onChange: (e) => {
              setValue(fields.PLUS_3_ANS, '');
              setValue(fields.FIRST_VALEUR, e.target.value);
            },
          }}
        />
      </div>
      {!!valeur && Number(valeur) < minCrm && (
        <BulleAide
          title="Attention"
          // why: le legacy n'affichait le bandeau en rouge (type danger) que pour le bonus moto
          className={type === 'moto' ? 'bg-error-50 text-error-900' : undefined}
          description={`Le bonus saisi est inférieur au bonus calculé automatiquement par rapport à vos dates de permis (valeur ${minCrm}). Merci de bien vérifier cette information avant de valider cette étape.`}
        />
      )}
    </div>
  );
}

/** Bonus 0.50 depuis plus de 3 ans (à afficher si le CRM vaut 0.50). */
export function Crm50Plus3AnsContent({ type }: CrmContentProps) {
  return (
    <div className="w-full flex flex-col items-center space-y-4">
      <Question.Header>
        <Star />
        <Question.Title>
          Avez vous ce <b>bonus {type}</b> de 0.50 depuis <b>plus de 3 ans</b> ?
        </Question.Title>
      </Question.Header>
      <FieldRadioGroup
        name={CRM_FIELDS[type].PLUS_3_ANS}
        options={OUI_NON_STRING_OPTIONS}
        // why: ValeurCrm remet la réponse à '' (non bloqué par le isRequired du groupe qui ne teste que null/undefined)
        validation={{ validate: (value: unknown) => (value === '' ? 'Ce champs est requis' : undefined) }}
      />
    </div>
  );
}
